/**
 * Hand-tokenized sample SQL for the Settings → Editor colors dialog's
 * preview panes (Slice 3 dialog iteration). Pre-tokenized by hand rather
 * than run through Monaco's own tokenizer — the pane is a cheap, static
 * approximation of what an editor would look like, not a second live Monaco
 * instance, so it intentionally skips Monaco-only effects (bracket-pair
 * colorization, semantic highlighting, etc.).
 *
 * Token choices mirror the ACTIVE tokenizer's actual behavior (see the
 * "which tokenizer is active" note on `buildMonacoTokenRules` in
 * `monacoColorSchemes.ts`): `COUNT`/`SUM` are builtin functions → `predefined`
 * (not `keyword`); a double-quoted alias is `string.double`; a line comment
 * is one `comment` segment; a block comment splits into `comment.quote`
 * opening/closing delimiters around a `comment` body.
 */
import type { EditorColorSchemeTokenColors } from './monacoColorSchemes';

export interface PreviewSegment {
  text: string;
  /** `undefined` = punctuation/plain, rendered in the table's `identifier` color. */
  token?: keyof EditorColorSchemeTokenColors;
}

// One entry per source line. Covers all 9 `EditorColorSchemeTokenColors`
// classes at least once — see `schemePreviewSample.test.ts`. Rendered SQL:
//
//   -- Revenue by customer
//   SELECT c.name AS "customer",
//          COUNT(o.id) AS orders,
//          SUM(o.total) * 1.23 AS gross
//   FROM customers c
//   JOIN orders o ON o.customer_id = c.id
//   WHERE o.status = 'shipped'  (a block comment follows: "paid only")
export const PREVIEW_SQL_SEGMENTS: PreviewSegment[][] = [
  [{ text: '-- Revenue by customer', token: 'comment' }],
  [
    { text: 'SELECT', token: 'keyword' },
    { text: ' ' },
    { text: 'c', token: 'identifier' },
    { text: '.' },
    { text: 'name', token: 'identifier' },
    { text: ' ' },
    { text: 'AS', token: 'keyword' },
    { text: ' ' },
    { text: '"customer"', token: 'string.double' },
    { text: ',' },
  ],
  [
    { text: '       ' },
    { text: 'COUNT', token: 'predefined' },
    { text: '(' },
    { text: 'o', token: 'identifier' },
    { text: '.' },
    { text: 'id', token: 'identifier' },
    { text: ')' },
    { text: ' ' },
    { text: 'AS', token: 'keyword' },
    { text: ' ' },
    { text: 'orders', token: 'identifier' },
    { text: ',' },
  ],
  [
    { text: '       ' },
    { text: 'SUM', token: 'predefined' },
    { text: '(' },
    { text: 'o', token: 'identifier' },
    { text: '.' },
    { text: 'total', token: 'identifier' },
    { text: ')' },
    { text: ' ' },
    { text: '*', token: 'operator' },
    { text: ' ' },
    { text: '1.23', token: 'number' },
    { text: ' ' },
    { text: 'AS', token: 'keyword' },
    { text: ' ' },
    { text: 'gross', token: 'identifier' },
  ],
  [
    { text: 'FROM', token: 'keyword' },
    { text: ' ' },
    { text: 'customers', token: 'identifier' },
    { text: ' ' },
    { text: 'c', token: 'identifier' },
  ],
  [
    { text: 'JOIN', token: 'keyword' },
    { text: ' ' },
    { text: 'orders', token: 'identifier' },
    { text: ' ' },
    { text: 'o', token: 'identifier' },
    { text: ' ' },
    { text: 'ON', token: 'keyword' },
    { text: ' ' },
    { text: 'o', token: 'identifier' },
    { text: '.' },
    { text: 'customer_id', token: 'identifier' },
    { text: ' ' },
    { text: '=', token: 'operator' },
    { text: ' ' },
    { text: 'c', token: 'identifier' },
    { text: '.' },
    { text: 'id', token: 'identifier' },
  ],
  [
    { text: 'WHERE', token: 'keyword' },
    { text: ' ' },
    { text: 'o', token: 'identifier' },
    { text: '.' },
    { text: 'status', token: 'identifier' },
    { text: ' ' },
    { text: '=', token: 'operator' },
    { text: ' ' },
    { text: "'shipped'", token: 'string' },
    { text: ' ' },
    { text: '/*', token: 'comment.quote' },
    { text: ' paid only ', token: 'comment' },
    { text: '*/', token: 'comment.quote' },
  ],
];
